import { StateCreator } from 'zustand';
import {
  getCurrentUser,
  client,
  updateAgency,
  API_BASE_URL,
} from '../../services/api';
import type { AgencyUpdate, HnpUploadTab } from '../../services/api/types.gen';
import { setUserContext, addBreadcrumb } from '../../utils/sentry';
import type { FeatureFlagType } from '../../constants/featureFlags';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  feature_flags: string[];
  agency?: {
    id: string;
    system_name: string;
    written_name: string;
    abbreviation: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    phone_number?: string | null;
    hnp_upload_tab?: HnpUploadTab | null;
    hnp_upload_task?: string | null;
  };
}

export interface AuthSlice {
  user: User | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  authChecked: boolean;
  loginWithWorkOS: () => Promise<void>;
  logout: () => void;
  setAuthLoading: (loading: boolean) => void;
  checkAuthStatus: () => Promise<void>;
  checkDevAutoLogin: () => Promise<boolean>;
  updateAgencyInfo: (
    agencyId: string,
    agencyData: Pick<
      AgencyUpdate,
      'address' | 'city' | 'state' | 'zip_code' | 'phone_number'
    >
  ) => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasFeatureFlag: (featureFlag: FeatureFlagType) => boolean;
}

export const authSlice: StateCreator<AuthSlice> = (set, get) => ({
  user: null,
  isAuthenticated: false,
  isAuthLoading: false,
  authChecked: false,

  setAuthLoading: (loading: boolean) => {
    set({ isAuthLoading: loading });
  },

  checkDevAutoLogin: async (): Promise<boolean> => {
    // Check for dev auto-login when user is not authenticated
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true') {
      // Use configured redirect path, or default to current path
      let redirectPath = import.meta.env.VITE_DEV_AUTO_LOGIN_REDIRECT;

      if (!redirectPath) {
        // Get current path for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        // Default to /dashboard for root path or login redirects
        redirectPath =
          currentPath === '/' || currentPath === '/login'
            ? '/dashboard'
            : currentPath;
      }

      // Use fetch to authenticate via proxy (cookie gets set with localhost domain)
      // Then manually redirect to the target path
      try {
        const response = await fetch(
          `${API_BASE_URL}/dev/auto-login?redirect_to=${encodeURIComponent(redirectPath)}`,
          {
            credentials: 'include',
          }
        );
        const data = await response.json();

        if (data.success) {
          // Cookie is now set, redirect to the target path
          window.location.href = data.redirect_url;
        } else {
          console.error('Dev auto-login failed:', data);
        }
      } catch (error) {
        console.error('Dev auto-login fetch error:', error);
      }

      return true; // Indicate that we initiated auto-login
    }
    return false; // No auto-login performed
  },

  loginWithWorkOS: async (): Promise<void> => {
    set({ isAuthLoading: true });

    try {
      window.location.href = `${API_BASE_URL}/auth/login`;
    } catch (error) {
      console.error('WorkOS login error:', error);
      set({ isAuthLoading: false });
    }
  },

  logout: (): void => {
    try {
      addBreadcrumb('User logout initiated', 'auth');

      // Clear persisted state from localStorage before logout
      localStorage.removeItem('roger-emr-store');

      // Redirect to logout endpoint - this allows the backend
      // to handle the WorkOS logout properly by disabling the JWT cookie
      window.location.href = `${API_BASE_URL}/auth/logout`;
    } catch (error) {
      console.error('Logout error:', error);
      addBreadcrumb('User logout error', 'auth', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Even if logout fails, clear local state and persisted state
      localStorage.removeItem('roger-emr-store');
      set({
        user: null,
        isAuthenticated: false,
        authChecked: true,
      });
    }
  },

  checkAuthStatus: async (): Promise<void> => {
    try {
      set({ isAuthLoading: true });

      const userData = await getCurrentUser({
        client,
      });

      if (userData && userData.data) {
        // Type assertion for the response data
        const userResponse = userData.data as {
          id?: string;
          email?: string;
          name?: string;
          first_name?: string;
          last_name?: string;
          role?: string;
          permissions?: string[];
          feature_flags?: string[];
          agency?: {
            id: string;
            system_name: string;
            written_name: string;
            abbreviation: string;
            address?: string | null;
            city?: string | null;
            state?: string | null;
            zip_code?: string | null;
            phone_number?: string | null;
            hnp_upload_tab?: HnpUploadTab | null;
            hnp_upload_task?: string | null;
          };
        };

        // Create a User object from the response
        const user: User = {
          id: userResponse.id || '',
          email: userResponse.email || '',
          name:
            userResponse.name ||
            `${userResponse.first_name || ''} ${userResponse.last_name || ''}`.trim(),
          role: userResponse.role || 'clinician',
          permissions: userResponse.permissions || [],
          feature_flags: userResponse.feature_flags || [],
          agency: userResponse.agency,
        };

        // Log user permissions for debugging
        console.log('User Authenticated:', {
          email: user.email,
          name: user.name,
          role: user.role,
          permissions: user.permissions,
          feature_flags: user.feature_flags || [],
          agency: user.agency,
        });

        // Set Sentry user context
        setUserContext({
          id: user.id,
          email: user.email,
          username: user.name,
        });

        addBreadcrumb('Auth status check successful', 'auth', {
          userId: user.id,
        });

        set({
          user,
          isAuthenticated: true,
          isAuthLoading: false,
          authChecked: true,
        });
      } else {
        // Check for dev auto-login before marking as unauthenticated
        const didRedirect = await get().checkDevAutoLogin();

        if (!didRedirect) {
          set({
            user: null,
            isAuthenticated: false,
            isAuthLoading: false,
            authChecked: true,
          });
        }
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      addBreadcrumb('Auth status check error', 'auth', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Check for dev auto-login before marking as unauthenticated
      const didRedirect = await get().checkDevAutoLogin();

      if (!didRedirect) {
        set({
          user: null,
          isAuthenticated: false,
          isAuthLoading: false,
          authChecked: true,
        });
      }
    }
  },

  updateAgencyInfo: async (
    agencyId: string,
    agencyData: Pick<
      AgencyUpdate,
      'address' | 'city' | 'state' | 'zip_code' | 'phone_number'
    >
  ): Promise<boolean> => {
    try {
      const response = await updateAgency({
        body: agencyData,
        path: {
          agency_id: agencyId,
        },
        client,
      });

      if (response.data) {
        // Update the user's agency information in the store
        set(state => ({
          user: state.user
            ? {
                ...state.user,
                agency: state.user.agency
                  ? {
                      ...state.user.agency,
                      ...response.data,
                    }
                  : undefined,
              }
            : null,
        }));

        addBreadcrumb('Agency info updated successfully', 'auth', {
          agencyId,
          updatedFields: Object.keys(agencyData),
        });

        return true;
      }
      return false;
    } catch (error) {
      console.error('Update agency info error:', error);
      addBreadcrumb('Update agency info error', 'auth', {
        agencyId,
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  },

  hasPermission: (permission: string) =>
    get().user?.permissions?.includes(permission) || false,

  hasAnyPermission: (permissions: string[]) =>
    permissions.some(permission =>
      get().user?.permissions?.includes(permission)
    ),

  hasFeatureFlag: (featureFlag: FeatureFlagType) =>
    (get().user?.feature_flags || []).includes(featureFlag) || false,
});
